/** Treasury hooks. API data only when authenticated (errors → isError + empty). */

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { TREASURY_QUERY_PREFIX, queryKeys } from "@/lib/query-client";
import type {
  TreasuryAccount,
  TreasuryBalanceAdjustment,
  TreasuryMovement,
  TreasuryReconciliation,
} from "@/types";
import { useAuthStore } from "@/features/auth/auth.store";
import {
  prependToList,
  removeFromList,
  restoreSnapshot,
  snapshotList,
} from "@/lib/optimistic-cache";
import { treasuryApi } from "./treasury.api";
import type { ApiMovement } from "./treasury.adapter";
import {
  toTreasuryAccount,
  toTreasuryBalanceAdjustment,
  toTreasuryMovement,
  toTreasuryReconciliation,
  type AdjustBalanceInput,
  type MovementInput,
  type TransferInput,
} from "./treasury.adapter";

const MOVEMENTS_PARAMS = { limit: 100, sortBy: "date", sortOrder: "desc" as const };

export type DataSource = "api" | "mock";

/** Warm the treasury caches (startup warmup — see layout/useWarmupCoreData). */
export function prefetchTreasury(qc: QueryClient): Promise<void> {
  return Promise.all([
    qc.prefetchQuery({ queryKey: queryKeys.treasuryAccounts, queryFn: () => treasuryApi.listAccounts() }),
    qc.prefetchQuery({
      queryKey: queryKeys.treasuryMovements(MOVEMENTS_PARAMS),
      queryFn: () => treasuryApi.listMovements(MOVEMENTS_PARAMS),
    }),
  ]).then(() => undefined);
}

export function useTreasuryAccounts(): {
  accounts: TreasuryAccount[];
  totals: Record<string, number>;
  isLoading: boolean;
  isError: boolean;
  source: DataSource;
  refetch: () => void;
} {
  const status = useAuthStore((s) => s.status);
  const authed = status === "authenticated";

  const query = useQuery({
    queryKey: queryKeys.treasuryAccounts,
    queryFn: () => treasuryApi.listAccounts(),
    enabled: authed,
  });

  if (query.data)
    return {
      accounts: query.data.data.map(toTreasuryAccount),
      totals: query.data.totals,
      isLoading: false,
      isError: false,
      source: "api",
      refetch: query.refetch,
    };
  if (query.isError)
    return {
      accounts: [],
      totals: {},
      isLoading: false,
      isError: true,
      source: "api",
      refetch: query.refetch,
    };
  return {
    accounts: [],
    totals: {},
    isLoading: query.isLoading,
    isError: false,
    source: "api",
    refetch: query.refetch,
  };
}

export function useTreasuryMovements(): {
  movements: TreasuryMovement[];
  isLoading: boolean;
  isError: boolean;
  source: DataSource;
  refetch: () => void;
} {
  const status = useAuthStore((s) => s.status);
  const authed = status === "authenticated";

  const query = useQuery({
    queryKey: queryKeys.treasuryMovements(MOVEMENTS_PARAMS),
    queryFn: () => treasuryApi.listMovements(MOVEMENTS_PARAMS),
    enabled: authed,
  });

  if (query.data)
    return {
      movements: query.data.data.map(toTreasuryMovement),
      isLoading: false,
      isError: false,
      source: "api",
      refetch: query.refetch,
    };
  if (query.isError)
    return {
      movements: [],
      isLoading: false,
      isError: true,
      source: "api",
      refetch: query.refetch,
    };
  return {
    movements: [],
    isLoading: query.isLoading,
    isError: false,
    source: "api",
    refetch: query.refetch,
  };
}

export function useTreasuryReconciliations(): {
  reconciliations: TreasuryReconciliation[];
  isLoading: boolean;
} {
  const authed = useAuthStore((s) => s.status) === "authenticated";
  const query = useQuery({
    queryKey: queryKeys.treasuryReconciliations,
    queryFn: () => treasuryApi.listReconciliations(),
    enabled: authed,
  });
  if (query.data)
    return { reconciliations: query.data.map(toTreasuryReconciliation), isLoading: false };
  return { reconciliations: [], isLoading: authed && query.isLoading };
}

/**
 * Historique des ajustements. Sans `accountId`, la liste est globale (bloc de
 * la page) ; avec, elle est celle du compte inspecté. `enabled` évite de tirer
 * la liste d'un compte tant que la modale qui l'affiche est fermée.
 */
export function useTreasuryAdjustments(
  accountId?: string,
  enabled = true,
): {
  adjustments: TreasuryBalanceAdjustment[];
  isLoading: boolean;
} {
  const authed = useAuthStore((s) => s.status) === "authenticated";
  const query = useQuery({
    queryKey: queryKeys.treasuryAdjustments(accountId),
    queryFn: () => treasuryApi.listAdjustments(accountId),
    enabled: authed && enabled,
  });
  if (query.data)
    return { adjustments: query.data.map(toTreasuryBalanceAdjustment), isLoading: false };
  return { adjustments: [], isLoading: authed && query.isLoading };
}

function useInvalidateTreasury() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: TREASURY_QUERY_PREFIX });
    // Mouvements/transferts/ajustements changent les soldes que la Vue
    // d'ensemble compta et le dashboard recalculent (ADR-001).
    void qc.invalidateQueries({ queryKey: ["accounting"] });
    void qc.invalidateQueries({ queryKey: ["reports"] });
  };
}

export function useCreateTreasuryAccount() {
  const invalidate = useInvalidateTreasury();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      currency: string;
      openingBalance: number;
      note?: string;
      // spec compta §6.1
      accountType?: string;
      responsibleId?: string;
      bankName?: string;
      numberLast?: string;
      color?: string;
      allowNegative?: boolean;
    }) => {
      return treasuryApi.createAccount(input);
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}

/**
 * Modification d'un compte — solde d'ouverture COMPRIS.
 *
 * Le solde affiché vaut `solde d'ouverture + mouvements` : sans cette écriture,
 * un compte créé avec une ouverture de 10 000 gardait 10 000 même après
 * suppression de tous ses mouvements, sans aucun moyen de le corriger
 * (le formulaire n'existait qu'en création). L'API l'acceptait déjà.
 */
export function useUpdateTreasuryAccount() {
  const invalidate = useInvalidateTreasury();
  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: {
      id: string;
      name?: string;
      currency?: string;
      openingBalance?: number;
      note?: string;
      accountType?: string;
      responsibleId?: string;
      bankName?: string;
      numberLast?: string;
      color?: string;
      allowNegative?: boolean;
    }) => {
      return treasuryApi.updateAccount(id, input);
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useCreateTreasuryMovement() {
  const qc = useQueryClient();
  const invalidate = useInvalidateTreasury();
  return useMutation({
    mutationFn: async (input: MovementInput) => {
      return treasuryApi.createMovement({ ...input });
    },
    // The new movement shows at the top of the list immediately; account
    // balances are recomputed server-side, so they follow via the refetch.
    onSuccess: (data) => {
      prependToList<ApiMovement>(qc, queryKeys.treasuryMovements(MOVEMENTS_PARAMS), data as ApiMovement);
      void invalidate();
    },
  });
}

export function useTreasuryTransfer() {
  const qc = useQueryClient();
  const invalidate = useInvalidateTreasury();
  return useMutation({
    mutationFn: async (input: TransferInput) => {
      return treasuryApi.transfer({ ...input });
    },
    onSuccess: (data) => {
      prependToList<ApiMovement>(qc, queryKeys.treasuryMovements(MOVEMENTS_PARAMS), data as ApiMovement);
      void invalidate();
    },
  });
}

export function useReconcileAccount() {
  const invalidate = useInvalidateTreasury();
  return useMutation({
    mutationFn: async (input: {
      accountId: string;
      month: string;
      realBalance: number;
      reason?: string;
    }) => {
      return treasuryApi.reconcile({ ...input, month: `${input.month}-01` });
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useAdjustTreasuryBalance() {
  const invalidate = useInvalidateTreasury();
  return useMutation({
    mutationFn: async ({ accountId, ...body }: AdjustBalanceInput) => {
      return treasuryApi.adjustBalance(accountId, body);
    },
    // Le solde ET le relevé bougent (un mouvement CORRECTION est posté côté
    // serveur) : on invalide tout le préfixe plutôt que de patcher le cache.
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useArchiveTreasuryMovement() {
  const qc = useQueryClient();
  const invalidate = useInvalidateTreasury();
  return useMutation({
    mutationFn: async (id: string) => {
      return treasuryApi.archiveMovement(id);
    },
    onMutate: async (id: string) => {
      const listKey = queryKeys.treasuryMovements(MOVEMENTS_PARAMS);
      await qc.cancelQueries({ queryKey: listKey });
      const snapshot = snapshotList(qc, [listKey]);
      removeFromList<ApiMovement>(qc, listKey, id);
      return { snapshot };
    },
    onError: (_err, _id, context) => {
      if (context?.snapshot) restoreSnapshot(qc, context.snapshot);
    },
    onSettled: () => {
      void invalidate();
    },
  });
}
